import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  Badge,
  CircularProgress,
  Alert,
  Stack,
  Divider,
  Paper,
  IconButton,
  InputAdornment
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Send as SendIcon,
  AttachFile as AttachIcon,
  Pets as PetsIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { api } from '@services/authService';

interface Thread {
  id: string;
  subject: string;
  status: string;
  last_message_at: string;
  unread_count: number;
  patient?: any;
  owner?: any;
  last_message?: any;
}

interface Message {
  id: string;
  body: string;
  sender_type: string;
  sender?: any;
  created_at: string;
  attachments: any[];
}

const DoctorMessages: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [threads, setThreads] = useState<Thread[]>([]);
  const [selectedThread, setSelectedThread] = useState<Thread | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [messageText, setMessageText] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    loadThreads();
  }, []);

  useEffect(() => {
    if (selectedThread) {
      loadMessages(selectedThread.id);
    }
  }, [selectedThread]);

  const loadThreads = async () => {
    try {
      setLoading(true);
      const response = await api.get('/doctor/messages/threads');
      setThreads(response.data.data.threads);
    } catch (error) {
      console.error('Eroare la incarcarea thread-urilor:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async (threadId: string) => {
    try {
      const response = await api.get(`/doctor/messages/threads/${threadId}`);
      setMessages(response.data.data);
    } catch (error) {
      console.error('Eroare la incarcarea mesajelor:', error);
    }
  };

  const handleSendMessage = async () => {
    if (!messageText.trim() || !selectedThread) return;

    try {
      setSending(true);
      await api.post('/doctor/messages', {
        thread_id: selectedThread.id,
        body: messageText,
        sender_type: 'staff'
      });

      setMessageText('');
      loadMessages(selectedThread.id);
      loadThreads(); // Refresh pentru a actualiza last_message
    } catch (error) {
      console.error('Eroare la trimiterea mesajului:', error);
    } finally {
      setSending(false);
    }
  };

  const handleThreadClick = (thread: Thread) => {
    setSelectedThread(thread);
  };

  return (
    <Box sx={{ p: 3, height: 'calc(100vh - 100px)' }}>
      <Typography variant="h4" gutterBottom>
        Mesaje
      </Typography>

      <Grid container spacing={2} sx={{ height: 'calc(100% - 60px)' }}>
        {/* Thread List */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flexGrow: 1, overflow: 'auto' }}>
              {loading ? (
                <Box display="flex" justifyContent="center" py={4}>
                  <CircularProgress />
                </Box>
              ) : threads.length === 0 ? (
                <Alert severity="info">Nu exista mesaje</Alert>
              ) : (
                <List>
                  {threads.map((thread) => (
                    <React.Fragment key={thread.id}>
                      <ListItemButton
                        selected={selectedThread?.id === thread.id}
                        onClick={() => handleThreadClick(thread)}
                      >
                        <ListItemAvatar>
                          <Badge badgeContent={thread.unread_count} color="error">
                            <Avatar>
                              {thread.patient ? <PetsIcon /> : <PersonIcon />}
                            </Avatar>
                          </Badge>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Box display="flex" justifyContent="space-between">
                              <Typography variant="subtitle2">
                                {thread.patient?.name || thread.owner?.firstName}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {format(new Date(thread.last_message_at), 'HH:mm')}
                              </Typography>
                            </Box>
                          }
                          secondary={
                            <Typography variant="body2" noWrap>
                              {thread.last_message?.body || 'Fara mesaje'}
                            </Typography>
                          }
                        />
                      </ListItemButton>
                      <Divider />
                    </React.Fragment>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Messages View */}
        <Grid size={{ xs: 12, md: 8 }}>
          {!selectedThread ? (
            <Card sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CardContent>
                <Alert severity="info">Selecteaza un thread pentru a vedea mesajele</Alert>
              </CardContent>
            </Card>
          ) : (
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              {/* Thread Header */}
              <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box display="flex" alignItems="center" gap={2}>
                    <Avatar>
                      {selectedThread.patient ? <PetsIcon /> : <PersonIcon />}
                    </Avatar>
                    <Box>
                      <Typography variant="h6">
                        {selectedThread.patient?.name || selectedThread.owner?.firstName}
                      </Typography>
                      {selectedThread.patient && (
                        <Typography variant="caption" color="text.secondary">
                          Proprietar: {selectedThread.owner?.firstName} {selectedThread.owner?.lastName}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  <Chip label={selectedThread.status} size="small" />
                </Box>
              </Box>

              {/* Messages */}
              <Box sx={{ flexGrow: 1, overflow: 'auto', p: 2 }}>
                <Stack spacing={2}>
                  {messages.map((message) => (
                    <Box
                      key={message.id}
                      sx={{
                        display: 'flex',
                        justifyContent: message.sender_type === 'staff' ? 'flex-end' : 'flex-start'
                      }}
                    >
                      <Paper
                        sx={{
                          p: 2,
                          maxWidth: '70%',
                          bgcolor: message.sender_type === 'staff' ? 'primary.light' : 'background.paper'
                        }}
                      >
                        <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                          {message.sender?.firstName || 'Client'} •{' '}
                          {format(new Date(message.created_at), 'HH:mm')}
                        </Typography>
                        <Typography variant="body2">{message.body}</Typography>
                        {message.attachments && message.attachments.length > 0 && (
                          <Box mt={1}>
                            {message.attachments.map((att: any, index: number) => (
                              <Chip key={index} label={att.name} size="small" icon={<AttachIcon />} />
                            ))}
                          </Box>
                        )}
                      </Paper>
                    </Box>
                  ))}
                </Stack>
              </Box>

              {/* Message Input */}
              <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
                <Stack direction="row" spacing={1}>
                  <TextField
                    fullWidth
                    placeholder="Scrie un mesaj..."
                    value={messageText}
                    onChange={(e) => setMessageText(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                    multiline
                    maxRows={3}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton size="small">
                            <AttachIcon />
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />
                  <Button
                    variant="contained"
                    endIcon={<SendIcon />}
                    onClick={handleSendMessage}
                    disabled={sending || !messageText.trim()}
                  >
                    Trimite
                  </Button>
                </Stack>
              </Box>
            </Card>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default DoctorMessages;

